import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatPaginator, MatPaginatorModule, MatPaginatorSelectConfig, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { EVENT_CELL_COLUMN_DEF, SELECT_CELL_COLUMN_DEF } from './table.constants';
import { SelectionModel } from '@angular/cdk/collections';
import { TABLE_SELECTOR_HANDLER, TableSelectionHandler } from './table-selection-handler';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ToDisplayedColumnsPipe } from './to-displayed-columns.pipe';
import { CommonModule } from '@angular/common';
import { SortDataTransformer, TableColumn } from './table.model';
import { CellRendererComponent } from './cell-renderer/cell-renderer.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TableRowDisabledPipe } from './table-row-disabled/table-row-disabled.pipe';
import { RowColumnTooltipPipe } from './row-column-tooltip/row-column-tooltip.pipe';
import { MatRadioButton } from '@angular/material/radio';

@UntilDestroy()
@Component({
  selector: 'cbre-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatCheckboxModule,
    MatSortModule,
    CellRendererComponent,
    MatTooltipModule,
    ToDisplayedColumnsPipe,
    TableRowDisabledPipe,
    RowColumnTooltipPipe,
    MatRadioButton,
  ],
})
export class TableComponent<T = Record<string, any>> implements OnChanges, OnInit, AfterViewInit, OnDestroy {
  public readonly EVENT_CELL_COLUMN_DEF = EVENT_CELL_COLUMN_DEF;
  public readonly SELECT_CELL_COLUMN_DEF = SELECT_CELL_COLUMN_DEF;

  /**
   * Selection object
   */
  public selection: SelectionModel<T> = null;

  /**
   * Table data source
   */
  public dataSource: MatTableDataSource<T> = null;

  /**
   * Dictionary of data transformer functions to be used by the DataSource
   */
  private sortTransformerMap: Record<string, SortDataTransformer> = {};

  /**
   * Used as an id for the table
   */
  @Input() tableId = 'cbre_';

  /**
   * Elements or table data source list
   */
  @Input() set elements(elements: T[]) {
    this.dataSource = new MatTableDataSource<T>(elements);
    // Set sorting configurations when the sorting is handle withing the table
    if (this.dataSource && Object.keys(this.sortTransformerMap).length > 0) {
      this.cdr.detectChanges();
      this.dataSource.sort = this.sortOptions;

      this.dataSource.sortingDataAccessor = (dataObject: Record<string, any>, field: string) => {
        const transformer = this.sortTransformerMap[field];
        const val = dataObject[field];
        return transformer != null ? transformer(val) : val;
      };
    }

    if (this.totalCount == null) {
      this.dataSource.paginator = this.paginator;
    }
  }

  get elements() {
    return this.dataSource?.data ?? [];
  }

  /**
   * Columns to use to render the table
   */
  @Input() columns: TableColumn[] = [];

  /**
   * Template to render the actions/links of top right side
   */
  @Input() toolboxLeftTpl: TemplateRef<any> = null;

  /**
   * Template to render the actions/links of top right side
   */
  @Input() toolboxRightTpl: TemplateRef<any> | null = null;

  /**
   * Template to use to render a cell
   */
  @Input() cellRendererTpl: TemplateRef<any> | null = null;

  /**
   * Template to use to render a cell
   */
  @Input() cellEventsTpl: TemplateRef<any> | null = null;

  /**
   * Template to use for the header when using the template for cell events
   */
  @Input() headerEventsTpl: TemplateRef<any> | null = null;

  @Input() sort: { sortBy?: string };
  /**
   * Wether to enable/disable selection and also what type of selection is
   */
  @Input() selectionMode: 'single' | 'multi' | null = null;

  /**
   * Hide the paginator
   */
  @Input() hidePaginator = false;

  /**
   * Whether to show the first/last buttons UI to the user
   */
  @Input() showFirstLastButtons = false;

  /**
   * Display custom message when there is no data in the table
   */
  @Input() noDataLabel = 'No data available';

  /**
   * Defaulting the page options and giving the user the option to send custom page sizes.
   */
  @Input() pageSizeOptions = [10, 25, 50];

  /**
   * Input used to configure paginator length.
   * NOTE that when a value is provided, the paginator object reference
   * is removed from the dataSource. This is done to avoid conflicts.
   *
   * When this input is null, the dataSource object will handle the paginator (e.g., total number of records
   * and page changes). This is useful when pagination is required to be handled locally.
   *
   * However, to handle async pagination, a totalCount value must be provided, so the pagination
   * is handled independently of the dataSource. In this case, the dataSource data could have less
   * records than the actual total count (this.dataSource.data < this.totalCount), which
   * is expected on async pagination.
   */
  @Input() totalCount: number = null;

  @Input() pageSize: number = null;

  @Input() maxHeight = 'inherit';

  /**
   * paginator select config
   * @see https://material.angular.io/components/paginator/api#MatPaginatorSelectConfig
   */
  @Input() paginatorSelectConfig: MatPaginatorSelectConfig = {
    panelClass: 'cbre-table-paginator',
    disableOptionCentering: true,
  };

  /**
   * If true, the mat-paginator element will use the {@link paginatorSelectConfig} value followed by the selected
   * Page Size as a css class.
   *
   * Example:
   * `<mat-select class="a-custom-class-10">` - When the current page size selection is 10 and the
   * {@link paginatorSelectConfig} is "a-custom-class"
   *
   */
  @Input() showPaginatorSelectedValueInClass = false;

  /**
   * Wheter or not enable the sticky attribute for the header
   */
  @Input() stickyHeader = false;

  /**
   * Wheter or not trigger the click event
   */
  @Input() isRowClickable = false;

  /**
   * Whether to highlight the selected row
   */
  @Input() highlightSelected = false;

  /**
   * Triggered when the sorting changes
   */
  @Output() sortChange: EventEmitter<Sort> = new EventEmitter();

  @Output() pageChange: EventEmitter<PageEvent> = new EventEmitter();

  @Output() rowClick: EventEmitter<T> = new EventEmitter();

  @ViewChild(MatPaginator) paginator: MatPaginator;

  @ViewChild(MatSort) sortOptions: MatSort;

  constructor(
    @Inject(TABLE_SELECTOR_HANDLER) public selectionHandler: TableSelectionHandler<Record<string, any>>,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.totalCount?.currentValue != null) {
      if (this.dataSource != null) {
        /*
         * remove paginator control from data source if a totalCount
         * is provided to avoid pagination conflicts
         */
        this.dataSource.paginator = null;
      }
    }
  }

  ngOnInit() {
    this.selection = new SelectionModel<T>(this.selectionMode === 'multi', []);
    this.selectionHandler.configure(this.dataSource, this.selection);
  }

  ngAfterViewInit() {
    if (this.dataSource) {
      this.dataSource.sort = this.sortOptions;
      if (this.totalCount == null) {
        /*
         * remove paginator control from data source if a totalCount
         * is provided to avoid pagination conflicts
         */
        this.dataSource.paginator = this.paginator;
      }
    }

    if (this.columns?.some(column => column.sortDataTransformerFn != null)) {
      // generate transformer map
      this.sortTransformerMap = this.columns.reduce((acc, column) => {
        const { sortDataTransformerFn, field } = column;
        acc[field] = sortDataTransformerFn;
        return acc;
      }, {});
    }

    this.sortOptions?.sortChange?.pipe(untilDestroyed(this)).subscribe(evt => this.sortChange.emit(evt));
  }

  ngOnDestroy(): void {
    this.sortOptions = null;
  }

  selectAllToggle() {
    this.selectionHandler.selectAllToggle();
  }

  selectionToggle(row: Record<string, any>) {
    this.selectionHandler.selectionToggle(row);
  }

  get isAllSelected() {
    return this.selection?.hasValue() && this.selectionHandler?.isAllSelected();
  }

  get isIndeterminate() {
    return this.selection?.hasValue() && !this.selectionHandler?.isAllSelected();
  }

  checkboxLabel(row?: T): string {
    return this.selectionHandler.checkboxLabel(row);
  }

  trackColumn(column: TableColumn): number | string {
    return column.columnId;
  }

  handleRowClick(row: T) {
    if (this.isRowClickable) {
      this.rowClick.emit(row);
    }
  }
}
